import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import Confetti from 'react-confetti';
import { 
    GetTriviaQuestions, 
    SubmitTrivia, 
    CheckDuplicateParticipant,
    LogScreenshot,
    TrackMetric, 
    GetPublicConfig,
    RecoverPass
} from '../../../utilities/DL/QuizzB9Zone';
import { 
    GetStoredPasses, 
    SavePassToStorage, 
    MaskPhone, 
    MaskEmail 
} from '../../../utilities/BL/QuizzB9Zone';
import { ValidateSqlInjection } from '../../../utilities/BL/Security';
import { PluckingRooster as PluckingRoosterGame } from './PluckingRooster/PluckingRooster';

const TERMS_TEXT = `TÉRMINOS Y CONDICIONES - CONVIVENCIA EN LA ZONA
1. Organizador: El Club Dorados de Sinaloa es el único responsable organizador de la dinámica "Convivencia en La Zona" y de la coordinación para el acceso a cancha.
2. Proveedor Tecnológico: Five Digital Minds e Interaktia actúan exclusivamente como proveedores de la infraestructura tecnológica y quedan eximidos de responsabilidades relacionadas con la logística en el estadio o entrega de beneficios.
3. Participación: Dinámica válida exclusivamente para aficionados presentes en el Estadio El Encanto durante el partido en curso.
4. Asignación de Pases: Los Pases Exclusivos se otorgan a los aficionados que respondan correctamente todas las preguntas de la trivia en el menor tiempo posible o logran obtener un mínimo de 25 puntos en la dinámica de Aplastuzo, hasta agotar el cupo máximo de ganadores establecido para la jornada (50).
5. Intransferibilidad y Verificación: Cada pase es personal, único e intransferible. El aficionado deberá mostrar su pase digital o captura con su código QR al finalizar el partido.
6. Descalificación: Intentos de registro múltiple o uso de identidades falsas serán descalificados automáticamente sin derecho a reclamo.
7. Decisiones: Las resoluciones del comité organizador del Club Dorados de Sinaloa son definitivas e inapelables.`;

const PRIVACY_TEXT = `AVISO DE PRIVACIDAD SIMPLIFICADO
Sus datos personales (Nombre Completo, Correo Electrónico y Teléfono celular) serán tratados por el Club Dorados de Sinaloa y la operadora tecnológica Five Digital Minds con la finalidad de:
1. Validar su participación en la dinámica "Convivencia en La Zona".
2. Emitir y verificar su Pase Exclusivo de acceso a cancha mediante código QR.
3. Contactarle en caso de aclaraciones o para brindarle información sobre beneficios y eventos del club.

Sus datos serán tratados con estrictas medidas de seguridad y confidencialidad. Al registrarse, usted autoriza el tratamiento de sus datos conforme a este aviso. Para más información sobre el ejercicio de sus derechos ARCO, consulte los canales oficiales del Club Dorados de Sinaloa.`;

const SOURCE_MAPPING = {
    obs: 'overlay_screen',
    seat: 'seats',
    bath: 'restrooms',
    qr: 'qr_flyer'
};

export const QuizzB9ZoneFan = () => {
    const [searchParams] = useSearchParams();
    const rawSource = searchParams.get('s') || searchParams.get('utm_medium') || searchParams.get('src') || 'direct';
    const sourceParam = SOURCE_MAPPING[rawSource] || rawSource;

    // Fases de la experiencia: 'loading' | 'register' | 'evaluating_duplicate' | 'trivia' | 'submitting' | 'gift_overlay' | 'pass_revealed' | 'already_participated' | 'thanks' | 'ended' | 'error'
    const [phase, setPhase] = useState('loading');
    const [questions, setQuestions] = useState([]);
    const [config, setConfig] = useState(null);
    const [storedPasses, setStoredPasses] = useState([]);
    const [activePassIndex, setActivePassIndex] = useState(0);

    // Estado del Formulario
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        acceptedTerms: false
    });
    const [validationErrors, setValidationErrors] = useState({});
    const [formStartTracked, setFormStartTracked] = useState(false);

    // Modales Legales
    const [showTermsModal, setShowTermsModal] = useState(false);
    const [showPrivacyModal, setShowPrivacyModal] = useState(false);

    // Estado de Recuperación de Pases (Modal & Rate Limiting)
    const [showRecoverModal, setShowRecoverModal] = useState(false);
    const [recoverQuery, setRecoverQuery] = useState('');
    const [recoverLoading, setRecoverLoading] = useState(false);
    const [recoverError, setRecoverError] = useState('');

    // Estado de la Trivia
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [userAnswers, setUserAnswers] = useState({}); // { questionId: optionId }
    const [selectedOptionTemp, setSelectedOptionTemp] = useState(null);
    const [isSavingImage, setIsSavingImage] = useState(false);

    // Estado del Resultado
    const [activePass, setActivePass] = useState(null);
    const [showConfetti, setShowConfetti] = useState(false);
    const [giftOpened, setGiftOpened] = useState(false);
    const [apiErrorMessage, setApiErrorMessage] = useState('');
    const [lastScoreAchieved, setLastScoreAchieved] = useState(0);

    // Timers Granulares
    const sessionStartTimeRef = useRef(Date.now());
    const triviaStartTimeRef = useRef(null);
    const questionStartTimeRef = useRef(null);
    const questionTimesRef = useRef([]);

    const getSessionId = () => {
        let sessId = sessionStorage.getItem('quizzb9zone_session_id');
        if (!sessId) {
            sessId = 'sess_' + Math.random().toString(36).substring(2, 9) + '_' + Date.now();
            sessionStorage.setItem('quizzb9zone_session_id', sessId);
        }
        return sessId;
    };

    const trackEvent = (eventName) => {
        const sessId = getSessionId();
        try {
            TrackMetric(eventName, sessId, sourceParam);
        } catch (e) {
            console.error('Local metric error:', e);
        }

        try {
            if (window.dataLayer) {
                window.dataLayer.push({
                    event: `quizzb9zone_${eventName}`,
                    session_id: sessId,
                    traffic_source: sourceParam,
                    timestamp: new Date().toISOString()
                });
            }
        } catch (e) {
            console.error('DataLayer tracking error:', e);
        }

        try {
            if (window.gtag) {
                window.gtag('event', `quizzb9zone_${eventName}`, {
                    session_id: sessId,
                    event_category: 'quizzb9zone_dynamic',
                    event_label: eventName,
                    traffic_source: sourceParam
                });
            }
        } catch (e) {
            console.error('GA4 tracking error:', e);
        }
    };

    const handleInputFocus = () => {
        if (!formStartTracked) {
            setFormStartTracked(true);
            trackEvent('form_start');
        }
    };

    useEffect(() => {
        sessionStartTimeRef.current = Date.now();
        trackEvent('page_view');

        const hasQrParam = searchParams.get('s') || searchParams.get('src') === 'qr';
        if (hasQrParam) {
            trackEvent('qr_scan');
        }

        const initDynamic = async () => {
            const existingPasses = GetStoredPasses();
            setStoredPasses(existingPasses);

            const cfgData = await GetPublicConfig();
            if (cfgData.success && cfgData.data) {
                setConfig(cfgData.data);
                if (cfgData.data.isTriviaActive === false) {
                    setPhase('ended');
                    return;
                }

                if (cfgData.data.gameType === 'TRIVIA') {
                    const qData = await GetTriviaQuestions();
                    if (qData.success && qData.data && qData.data.length > 0) {
                        setQuestions(qData.data);
                    } else if (qData.success && qData.data && qData.data.length === 0) {
                        setPhase('ended');
                        return;
                    }
                }
                setPhase('register');
            } else {
                setPhase('register');
            }
        };

        initDynamic();
    }, [sourceParam, searchParams]);

    // Listener de capturas de pantalla (PrintScreen / Cambio de Pestaña)
    useEffect(() => {
        if (phase !== 'pass_revealed' || !activePass) return;

        const handleKeyUp = (e) => {
            if (e.key === 'PrintScreen' || e.keyCode === 44) {
                LogScreenshot(activePass.participantId || null, activePass.passCode, activePass.name, 'KEYBOARD_SHORTCUT');
                trackEvent('screenshot_saved');
            }
        };

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'hidden') {
                LogScreenshot(activePass.participantId || null, activePass.passCode, activePass.name, 'HEURISTIC');
            }
        };

        window.addEventListener('keyup', handleKeyUp);
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            window.removeEventListener('keyup', handleKeyUp);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [phase, activePass]);

    // ==========================================
    // MANEJADORES DE REGISTRO & VALIDACIÓN REACTIVA
    // ==========================================
    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        if (validationErrors[name]) {
            setValidationErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handleStartDynamic = async (e) => {
        e.preventDefault();
        setValidationErrors({});

        const errors = {};
        if (!formData.name || formData.name.trim().length < 3) {
            errors.name = 'Por favor ingresa tu nombre completo (mínimo 3 caracteres).';
        }

        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        if (!formData.email || !formData.email.trim()) {
            errors.email = 'El correo electrónico es requerido.';
        } else if (!emailRegex.test(formData.email.trim())) {
            errors.email = 'Por favor ingresa un correo electrónico válido.';
        }

        const cleanPhone = formData.phone ? formData.phone.replace(/\D/g, '') : '';
        if (!cleanPhone || cleanPhone.length !== 10) {
            errors.phone = 'Por favor ingresa tu número telefónico a 10 dígitos.';
        }

        if (!formData.acceptedTerms) {
            errors.acceptedTerms = 'Debes aceptar los términos y condiciones y el aviso de privacidad para participar.';
        }

        if (Object.keys(errors).length > 0) {
            setValidationErrors(errors);
            return;
        }

        // Validación Anti-SQL Injection
        const isNameThreat = await ValidateSqlInjection(formData.name);
        const isEmailThreat = await ValidateSqlInjection(formData.email);
        const isPhoneThreat = await ValidateSqlInjection(formData.phone);

        if (isNameThreat || isEmailThreat || isPhoneThreat) {
            setValidationErrors({ name: 'Se detectaron caracteres no permitidos en el formulario.' });
            return;
        }

        // Verificación Preventiva de Duplicados
        setPhase('evaluating_duplicate');
        try {
            const dupRes = await CheckDuplicateParticipant(formData.email.trim(), cleanPhone, formData.name.trim());
            if (dupRes && dupRes.success && dupRes.exists) {
                setPhase('already_participated');
                return;
            }
        } catch (err) {
            console.warn("No se pudo verificar duplicidad preventivamente:", err);
        }

        if (config?.gameType === 'TRIVIA') {
            if (questions.length === 0) {
                const qData = await GetTriviaQuestions();
                if (qData.success && qData.data && qData.data.length > 0) {
                    setQuestions(qData.data);
                } else {
                    setPhase('register');
                    setValidationErrors({ name: 'No hay preguntas disponibles en este momento. Intenta más tarde.' });
                    return;
                }
            }

            trackEvent('form_complete');
            trackEvent('quiz_start');

            // Iniciar cronómetro de trivia
            triviaStartTimeRef.current = Date.now();
            questionStartTimeRef.current = Date.now();
            questionTimesRef.current = [];
            setCurrentQuestionIndex(0);
            setUserAnswers({});
            setSelectedOptionTemp(null);
            setPhase('trivia');
        } else {
            trackEvent('form_complete');
            trackEvent('game_start');

            triviaStartTimeRef.current = Date.now();
            setPhase('minigame');
        }
    };

    const handleMinigameFinish = (achievedScore) => {
        setLastScoreAchieved(achievedScore || 0);
        handleFinalSubmit(null, achievedScore || 0);
    };

    // ==========================================
    // MANEJADOR DE RECUPERACIÓN DE PASES
    // ==========================================
    const handleRecoverSubmit = async (e) => {
        e.preventDefault();
        if (!recoverQuery.trim()) {
            setRecoverError('Por favor ingresa tu correo o número de teléfono.');
            return;
        }

        setRecoverLoading(true);
        setRecoverError('');

        const result = await RecoverPass(recoverQuery.trim());
        setRecoverLoading(false);

        if (result.isRateLimited) {
            setRecoverError(result.message);
            return;
        }

        if (!result.success || !result.isWinner) {
            setRecoverError(result.message || 'No se encontró un pase ganador activo con esos datos.');
            return;
        }

        const passData = {
            participantId: result.participantId || null,
            name: result.name,
            email: result.email,
            phone: result.phone,
            passCode: result.passCode,
            verificationToken: result.verificationToken,
            matchTitle: result.matchTitle || config?.matchTitle || 'Dorados de Sinaloa',
            matchInstructions: result.matchInstructions || config?.matchInstructions,
            winnerRank: result.winnerRank,
            passViewMode: result.passViewMode || config?.passViewMode || 'COMPLETE',
            showPassQr: result.showPassQr !== undefined ? result.showPassQr : (config?.showPassQr ?? true),
            showPassUserData: result.showPassUserData !== undefined ? result.showPassUserData : (config?.showPassUserData ?? false),
            showPassLocationNote: result.showPassLocationNote !== undefined ? result.showPassLocationNote : (config?.showPassLocationNote ?? false),
            createdAt: new Date().toISOString()
        };

        SavePassToStorage(passData);
        const updatedPasses = GetStoredPasses();
        setStoredPasses(updatedPasses);
        setActivePass(passData);
        setActivePassIndex(0);
        setShowRecoverModal(false);
        trackEvent('pass_recovered');
        setPhase('pass_revealed');
    };

    // ==========================================
    // MANEJADORES DE TRIVIA Y TIEMPOS GRANULARES
    // ==========================================
    const handleSelectOption = (questionId, optionId) => {
        if (selectedOptionTemp !== null) return;
        setSelectedOptionTemp(optionId);

        if (document.activeElement && typeof document.activeElement.blur === 'function') {
            document.activeElement.blur();
        }

        // Medir tiempo de la pregunta actual
        const now = Date.now();
        const questionDuration = Math.max(0.1, Math.round(((now - (questionStartTimeRef.current || now)) / 1000) * 100) / 100);
        questionTimesRef.current.push({
            questionId,
            durationSeconds: questionDuration
        });

        const updatedAnswers = {
            ...userAnswers,
            [questionId]: optionId
        };
        setUserAnswers(updatedAnswers);

        setTimeout(() => {
            if (document.activeElement && typeof document.activeElement.blur === 'function') {
                document.activeElement.blur();
            }
            if (currentQuestionIndex < questions.length - 1) {
                setCurrentQuestionIndex(prev => prev + 1);
                setSelectedOptionTemp(null);
                questionStartTimeRef.current = Date.now();
            } else {
                handleFinalSubmit(updatedAnswers);
            }
        }, 320);
    };

    const handleFinalSubmit = async (finalAnswers, directScore) => {
        setPhase('submitting');
        trackEvent(directScore !== undefined ? 'game_complete' : 'quiz_complete');

        const now = Date.now();
        const totalSessionTimeSeconds = Math.max(0.1, Math.round(((now - sessionStartTimeRef.current) / 1000) * 100) / 100);
        const triviaDurationSeconds = Math.max(0.1, Math.round(((now - (triviaStartTimeRef.current || now)) / 1000) * 100) / 100);

        const answersList = finalAnswers ? Object.entries(finalAnswers).map(([qId, oId]) => ({
            questionId: parseInt(qId, 10),
            optionId: parseInt(oId, 10)
        })) : [];

        const payload = {
            name: formData.name.trim(),
            email: formData.email.trim(),
            phone: formData.phone.trim().replace(/\D/g, ''),
            acceptedTerms: formData.acceptedTerms,
            source: sourceParam,
            score: directScore !== undefined ? directScore : undefined,
            gameDurationSeconds: triviaDurationSeconds,
            answers: answersList,
            totalSessionTimeSeconds,
            triviaDurationSeconds,
            questionTimes: questionTimesRef.current
        };

        const result = await SubmitTrivia(payload);

        if (result && result.success && result.isWinner) {
            const passData = {
                participantId: result.participantId || null,
                name: formData.name.trim(),
                email: formData.email.trim(),
                phone: formData.phone.trim(),
                passCode: result.passCode,
                verificationToken: result.verificationToken,
                matchTitle: result.matchTitle || config?.matchTitle || 'Dorados de Sinaloa vs Rival',
                matchInstructions: result.matchInstructions || config?.matchInstructions,
                winnerRank: result.winnerRank,
                passViewMode: result.passViewMode || config?.passViewMode || 'COMPLETE',
                showPassQr: result.showPassQr !== undefined ? result.showPassQr : (config?.showPassQr ?? true),
                showPassUserData: result.showPassUserData !== undefined ? result.showPassUserData : (config?.showPassUserData ?? false),
                showPassLocationNote: result.showPassLocationNote !== undefined ? result.showPassLocationNote : (config?.showPassLocationNote ?? false),
                createdAt: new Date().toISOString()
            };

            SavePassToStorage(passData);
            const updatedPasses = GetStoredPasses();
            setStoredPasses(updatedPasses);
            setActivePass(passData);
            setActivePassIndex(0);

            setPhase('gift_overlay');
        } else if (result && result.code === 'ALREADY_PARTICIPATED') {
            setPhase('already_participated');
        } else if (result && result.success && !result.isWinner) {
            setPhase('thanks');
        } else {
            setPhase('error');
            setApiErrorMessage(result?.message || 'Ocurrió un error al procesar tu participación.');
        }
    };

    // ==========================================
    // ANIMACIÓN DE LA CAJA DE REGALO SVG
    // ==========================================
    const handleOpenGift = () => {
        if (giftOpened) return;
        setGiftOpened(true);

        const bow = document.getElementById('gift-bow');
        const lid = document.getElementById('gift-lid');
        const base = document.getElementById('gift-base');
        const giftContainer = document.querySelector('.gift-wiggle');

        if (giftContainer) {
            giftContainer.classList.remove('gift-wiggle');
        }

        if (bow) bow.style.animation = 'untieBow 0.6s forwards cubic-bezier(0.25, 1, 0.5, 1)';
        if (lid) lid.style.animation = 'openLid 0.7s forwards cubic-bezier(0.25, 1, 0.5, 1)';
        if (base) base.style.animation = 'dropBase 0.7s forwards cubic-bezier(0.25, 1, 0.5, 1)';

        setTimeout(() => {
            setPhase('pass_revealed');
            setShowConfetti(true);
            setTimeout(() => setShowConfetti(false), 8000);
        }, 650);
    };

    // ==========================================
    // DESCARGA DE CAPTURA DEL PASE DORADO
    // ==========================================
    const handleDownloadPassCard = async () => {
        const pass = activePass || storedPasses[activePassIndex];
        if (!pass || isSavingImage) return;

        // Priorizar configuración fresca del servidor si está disponible
        const showQr = config?.showPassQr !== undefined ? config.showPassQr : (pass?.showPassQr ?? true);
        const showUserData = config?.showPassUserData !== undefined ? config.showPassUserData : (pass?.showPassUserData ?? false);
        const showLocationNote = config?.showPassLocationNote !== undefined ? config.showPassLocationNote : (pass?.showPassLocationNote ?? false);

        setIsSavingImage(true);
        try {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            const width = 720;
            const cardMargin = 30;
            const cardW = width - (cardMargin * 2);

            // Calcular altura dinámica según elementos activos para mantener proporción estilizada
            let calculatedH = 370; // Header + Subtitle + Shimmer Banner
            if (showQr) calculatedH += 270;
            if (showUserData) calculatedH += 60;
            calculatedH += (showLocationNote ? 70 : 50) + 20; // Security Box + margin
            calculatedH += 85; // Sponsor header + logos container (Interaktia + Barrio 9)
            calculatedH += cardMargin + 20; // Bottom margin

            const height = calculatedH;
            canvas.width = width;
            canvas.height = height;

            // Fondo exterior
            const bgGrad = ctx.createLinearGradient(0, 0, width, height);
            bgGrad.addColorStop(0, '#0f172a');
            bgGrad.addColorStop(0.5, '#172554');
            bgGrad.addColorStop(1, '#020617');
            ctx.fillStyle = bgGrad;
            ctx.fillRect(0, 0, width, height);

            const cardH = height - (cardMargin * 2);
            const radius = 28;

            ctx.save();
            ctx.beginPath();
            ctx.roundRect(cardMargin, cardMargin, cardW, cardH, radius);
            ctx.fillStyle = '#ffffff';
            ctx.fill();
            ctx.lineWidth = 8;
            ctx.strokeStyle = '#d4af37';
            ctx.stroke();
            ctx.clip();

            // Encabezado
            ctx.textAlign = 'center';
            ctx.fillStyle = '#b38728';
            ctx.font = '900 42px Montserrat, Arial, sans-serif';
            ctx.fillText('¡FELICIDADES!', width / 2, 95);

            const divGrad = ctx.createLinearGradient(80, 115, width - 80, 115);
            divGrad.addColorStop(0, 'transparent');
            divGrad.addColorStop(0.5, '#d4af37');
            divGrad.addColorStop(1, 'transparent');
            ctx.fillStyle = divGrad;
            ctx.fillRect(80, 115, width - 160, 3);

            ctx.fillStyle = '#0f172a';
            ctx.font = '700 18px Montserrat, Arial, sans-serif';
            ctx.fillText("Ganaste una convivencia en 'La Zona' al finalizar el partido.", width / 2, 150);

            // Shimmer Banner
            const bannerX = 60;
            const bannerY = 175;
            const bannerW = width - 120;
            const bannerH = 135;

            const bannerGrad = ctx.createLinearGradient(bannerX, bannerY, bannerX + bannerW, bannerY + bannerH);
            bannerGrad.addColorStop(0, '#b38728');
            bannerGrad.addColorStop(0.5, '#d4af37');
            bannerGrad.addColorStop(1, '#b38728');

            ctx.beginPath();
            ctx.roundRect(bannerX, bannerY, bannerW, bannerH, 16);
            ctx.fillStyle = bannerGrad;
            ctx.fill();

            ctx.fillStyle = '#ffffff';
            ctx.font = '900 24px Montserrat, Arial, sans-serif';
            ctx.fillText('PASE EXCLUSIVO PERSONAL', width / 2, bannerY + 36);

            ctx.font = '600 16px Montserrat, Arial, sans-serif';
            ctx.fillText(pass.matchTitle || 'Dorados de Sinaloa', width / 2, bannerY + 62);

            const badgeW = 280;
            const badgeH = 42;
            const badgeX = (width - badgeW) / 2;
            const badgeY = bannerY + 76;

            ctx.beginPath();
            ctx.roundRect(badgeX, badgeY, badgeW, badgeH, 10);
            ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
            ctx.fill();

            ctx.fillStyle = '#172554';
            ctx.font = '900 24px monospace';
            ctx.fillText(pass.passCode || '#FIEXARA01', width / 2, badgeY + 30);

            let currentY = bannerY + bannerH + 20;

            if (showQr) {
                // Contenedor QR
                const qrContainerSize = 230;
                const qrContainerX = (width - qrContainerSize) / 2;
                const qrContainerY = currentY;

                ctx.beginPath();
                ctx.roundRect(qrContainerX, qrContainerY, qrContainerSize, qrContainerSize, 16);
                ctx.fillStyle = '#f8fafc';
                ctx.fill();
                ctx.lineWidth = 2.5;
                ctx.strokeStyle = '#e2e8f0';
                ctx.stroke();

                // Dibujar QR desde el SVG en el DOM
                const qrSvgElement = document.querySelector('#pass-card svg');
                if (qrSvgElement) {
                    const svgData = new XMLSerializer().serializeToString(qrSvgElement);
                    const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
                    const svgUrl = URL.createObjectURL(svgBlob);
                    const qrImg = new Image();

                    await new Promise((resolve) => {
                        qrImg.onload = () => {
                            ctx.drawImage(qrImg, qrContainerX + 15, qrContainerY + 15, qrContainerSize - 30, qrContainerSize - 30);
                            URL.revokeObjectURL(svgUrl);
                            resolve();
                        };
                        qrImg.onerror = () => resolve();
                        qrImg.src = svgUrl;
                    });
                }

                currentY += qrContainerSize + 15;
            }

            if (showUserData) {
                // Datos aficionado
                ctx.fillStyle = '#0f172a';
                ctx.font = '800 22px Montserrat, Arial, sans-serif';
                ctx.fillText(pass.name || '', width / 2, currentY + 18);

                ctx.fillStyle = '#64748b';
                ctx.font = '600 15px Montserrat, Arial, sans-serif';
                const maskedText = `${MaskEmail(pass.email)} | ${MaskPhone(pass.phone)}`;
                ctx.fillText(maskedText, width / 2, currentY + 40);

                currentY += 56;
            }

            // Recuadro Seguridad
            const secBoxX = 60;
            const secBoxY = currentY;
            const secBoxW = width - 120;
            const secBoxH = showLocationNote ? 65 : 46;

            ctx.beginPath();
            ctx.roundRect(secBoxX, secBoxY, secBoxW, secBoxH, 12);
            ctx.fillStyle = '#fef2f2';
            ctx.fill();
            ctx.lineWidth = 1.5;
            ctx.strokeStyle = '#fecaca';
            ctx.stroke();

            ctx.fillStyle = '#b91c1c';
            if (showLocationNote) {
                ctx.font = '900 15px Montserrat, Arial, sans-serif';
                ctx.fillText('MUESTRA ESTE PASE O UNA CAPTURA AL BAJAR A LA CANCHA', width / 2, secBoxY + 26);
                ctx.font = '700 13px Montserrat, Arial, sans-serif';
                ctx.fillText('TÚNEL 2 AL FINALIZAR EL PARTIDO', width / 2, secBoxY + 48);
            } else {
                ctx.font = '900 15px Montserrat, Arial, sans-serif';
                ctx.fillText('MUESTRA ESTE PASE O UNA CAPTURA AL BAJAR A LA CANCHA', width / 2, secBoxY + 28);
            }

            currentY += secBoxH + 18;

            // Sección Patrocinado por con logos oficiales (Interaktia & Barrio 9)
            ctx.fillStyle = '#94a3b8';
            ctx.font = '700 12px Montserrat, Arial, sans-serif';
            ctx.fillText('PATROCINADO POR', width / 2, currentY + 10);
            currentY += 22;

            const loadLocalImage = (src) => {
                return new Promise((resolve) => {
                    const img = new Image();
                    img.crossOrigin = 'anonymous';
                    img.onload = () => resolve(img);
                    img.onerror = () => resolve(null);
                    img.src = src;
                });
            };

            const [logo1, logo2] = await Promise.all([
                loadLocalImage('/img/quizzb9zone/logo.png'),
                loadLocalImage('/img/quizzb9zone/logo2.png')
            ]);

            const sponsorLogosH = 38;
            const sepW = 2;
            const sepH = 35;
            const gap = 20;

            if (logo1 && logo2) {
                const ratio1 = (logo1.width || 1) / (logo1.height || 1);
                const w1 = Math.min(110, sponsorLogosH * ratio1);
                const h1 = w1 / ratio1;

                const ratio2 = (logo2.width || 1) / (logo2.height || 1);
                const w2 = Math.min(110, sponsorLogosH * ratio2);
                const h2 = w2 / ratio2;

                const totalGroupW = w1 + gap + sepW + gap + w2;
                const startX = (width - totalGroupW) / 2;

                // Dibujar logo 1 (Interaktia)
                const y1 = currentY + (sponsorLogosH - h1) / 2;
                ctx.drawImage(logo1, startX, y1, w1, h1);

                // Dibujar separador vertical
                const sepX = startX + w1 + gap;
                const sepY = currentY + (sponsorLogosH - sepH) / 2;
                ctx.fillStyle = '#cbd5e1';
                ctx.fillRect(sepX, sepY, sepW, sepH);

                // Dibujar logo 2 (Barrio 9)
                const x2 = sepX + sepW + gap;
                const y2 = currentY + (sponsorLogosH - h2) / 2;
                ctx.drawImage(logo2, x2, y2, w2, h2);
            } else if (logo1) {
                const ratio1 = (logo1.width || 1) / (logo1.height || 1);
                const w1 = Math.min(140, sponsorLogosH * ratio1);
                const h1 = w1 / ratio1;
                ctx.drawImage(logo1, (width - w1) / 2, currentY + (sponsorLogosH - h1) / 2, w1, h1);
            } else if (logo2) {
                const ratio2 = (logo2.width || 1) / (logo2.height || 1);
                const w2 = Math.min(140, sponsorLogosH * ratio2);
                const h2 = w2 / ratio2;
                ctx.drawImage(logo2, (width - w2) / 2, currentY + (sponsorLogosH - h2) / 2, w2, h2);
            } else {
                ctx.fillStyle = '#94a3b8';
                ctx.font = '700 13px Montserrat, Arial, sans-serif';
                ctx.fillText('INTERAKTIA • BARRIO 9', width / 2, currentY + 18);
            }

            ctx.restore();

            canvas.toBlob((blob) => {
                if (!blob) return;
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                const cleanFolio = (pass.passCode || 'PASE').replace(/[^a-zA-Z0-9]/g, '');
                a.download = `Pase_Convivencia_${cleanFolio}.png`;
                a.href = url;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
            }, 'image/png');

            LogScreenshot(pass.participantId || null, pass.passCode, pass.name, 'BUTTON_DOWNLOAD');
            trackEvent('screenshot_saved');
        } catch (err) {
            console.error('Error al descargar el pase:', err);
        } finally {
            setIsSavingImage(false);
        }
    };

    // ==========================================
    // MODALES LEGALES
    // ==========================================
    const renderLegalModal = (title, content, setter) => {
        return (
            <div className="position-fixed top-0 start-0 w-100 h-100 bg-black bg-opacity-75 d-flex align-items-center justify-content-center px-3" style={{ zIndex: 11000 }}>
                <div className="card w-100 bg-white border-0 rounded-4 shadow-lg p-4" style={{ maxWidth: '500px', maxHeight: '80vh', overflowY: 'auto' }}>
                    <div className="d-flex justify-content-between align-items-center mb-3 border-bottom pb-2">
                        <h4 className="fw-bold text-dark mb-0 fs-5">{title}</h4>
                        <button type="button" className="btn-close" onClick={() => setter(false)}></button>
                    </div>
                    <div className="text-secondary small" style={{ whiteSpace: 'pre-wrap', textAlign: 'justify', lineHeight: '1.5' }}>
                        {content}
                    </div>
                    <button type="button" className="btn btn-danger w-100 py-2 rounded-3 fw-bold mt-4" onClick={() => setter(false)}>
                        Cerrar
                    </button>
                </div>
            </div>
        );
    };

    // Logos de patrocinadores
    const defaultLogos = (
        <div className="QuizzB9ZoneFan__SponsorLogos">
            <img src="/img/quizzb9zone/logo.png" alt="Patrocinador 1" onError={(e) => { e.target.style.display = 'none'; }} />
            <div style={{ width: '2px', height: '35px', backgroundColor: '#cbd5e1' }}></div>
            <img src="/img/quizzb9zone/logo2.png" alt="Patrocinador 2" onError={(e) => { e.target.style.display = 'none'; }} />
        </div>
    );

    const currentQuestion = questions[currentQuestionIndex];

    return (
        <div 
            className="QuizzB9ZoneFan d-flex flex-column justify-content-between min-vh-100"
            style={{
                backgroundImage: 'url(/img/page/backgrounds/background.png)',
                // backgroundImage: 'url(/img/page/backgrounds/barrio9/background-1.png)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat'
            }}
        >
            {showConfetti && <Confetti numberOfPieces={250} recycle={false} />}
            {showTermsModal && renderLegalModal('Términos y Condiciones', TERMS_TEXT, setShowTermsModal)}
            {showPrivacyModal && renderLegalModal('Aviso de Privacidad', PRIVACY_TEXT, setShowPrivacyModal)}

            {/* HEADER BRANDING */}
            <header className="w-100 text-center pt-3 pb-1">
                <img 
                    src="/img/page/adsl-five.png" 
                    className="img-fluid d-block mx-auto mb-2" 
                    style={{ maxHeight: '70px', objectFit: 'contain' }}
                    alt="ADSL / Five Digital Minds" 
                    onError={(e) => { e.target.src = "/img/page/adsl-five.png"; }}
                />
            </header>

            {/* BARRA SUPERIOR CON ACCESO A PASES GUARDADOS */}
            {storedPasses.length > 0 && phase !== 'gift_overlay' && phase !== 'submitting' && (
                <div className="text-center py-2 px-3 mb-2 bg-transparent border-0">
                    <button 
                        type="button"
                        className="btn btn-sm btn-outline-light text-uppercase fw-bold rounded-pill px-3 py-1"
                        style={{ fontSize: '0.8rem', letterSpacing: '0.05em' }}
                        onClick={() => {
                            setActivePass(storedPasses[0]);
                            setActivePassIndex(0);
                            setPhase('pass_revealed');
                        }}
                    >
                        <i className="bi bi-award-fill me-1"></i> Ver mis pases ganados ({storedPasses.length})
                    </button>
                </div>
            )}

            {/* ============================================================ */}
            {/* 1. FASE DE CARGA                                             */}
            {/* ============================================================ */}
            {phase === 'loading' && (
                <main className="d-flex flex-column align-items-center justify-content-center flex-grow-1" style={{ minHeight: '60vh' }}>
                    <div className="spinner-border text-warning mb-3" style={{ width: '3rem', height: '3rem' }} role="status"></div>
                    <p className="font-cinzel text-warning fs-5 fw-bold tracking-wider">Cargando dinámica...</p>
                </main>
            )}

            {/* ============================================================ */}
            {/* 2. FASE DE REGISTRO                                          */}
            {/* ============================================================ */}
            {phase === 'register' && (
                <main className="QuizzB9ZoneFan__RegisterContainer animate__animated animate__fadeIn flex-grow-1 w-100">
                    <div className="text-center mb-3">
                        <h1 className="font-cinzel text-3xl font-black text-dark mb-1">
                            Convivencia en La Zona
                        </h1>
                        <p className="text-dark">
                            {config?.gameType === 'TRIVIA'
                                ? 'Responde la trivia y gana un pase exclusivo a cancha'
                                : config?.winnerSelectionMode === 'SCORE_THRESHOLD'
                                ? '¡Juega y participa por un pase exclusivo a cancha!'
                                : '¡Juega y participa por un pase exclusivo a cancha!'}
                                {/* //? `¡Consigue ${config?.minScoreRequired ?? 25} puntos o más y gana tu pase a cancha!` */}
                        </p>
                    </div>

                    <div className="QuizzB9ZoneFan__Card">
                        <form onSubmit={handleStartDynamic} noValidate>
                            <div className="mb-3 text-start">
                                <label className="form-label small fw-bold text-white">
                                    Nombre completo
                                </label>
                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    onFocus={handleInputFocus}
                                    className={`form-control QuizzB9ZoneFan__Input ${validationErrors.name ? 'is-invalid border-danger' : ''}`}
                                />
                                {validationErrors.name && (
                                    <div className="invalid-feedback small d-block mt-1">{validationErrors.name}</div>
                                )}
                            </div>

                            <div className="mb-3 text-start">
                                <label className="form-label small fw-bold text-white">
                                    Correo electrónico
                                </label>
                                <input
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    onFocus={handleInputFocus}
                                    className={`form-control QuizzB9ZoneFan__Input ${validationErrors.email ? 'is-invalid border-danger' : ''}`}
                                />
                                {validationErrors.email && (
                                    <div className="invalid-feedback small d-block mt-1">{validationErrors.email}</div>
                                )}
                            </div>

                            <div className="mb-3 text-start">
                                <label className="form-label small fw-bold text-white">
                                    Teléfono / WhatsApp (10 dígitos)
                                </label>
                                <input
                                    type="tel"
                                    name="phone"
                                    maxLength="10"
                                    value={formData.phone}
                                    onChange={handleInputChange}
                                    onFocus={handleInputFocus}
                                    className={`form-control QuizzB9ZoneFan__Input ${validationErrors.phone ? 'is-invalid border-danger' : ''}`}
                                />
                                {validationErrors.phone && (
                                    <div className="invalid-feedback small d-block mt-1">{validationErrors.phone}</div>
                                )}
                            </div>

                            <div className="form-check mb-4 text-start">
                                <input
                                    type="checkbox"
                                    name="acceptedTerms"
                                    id="acceptTermsCheck"
                                    checked={formData.acceptedTerms}
                                    onChange={handleInputChange}
                                    className={`form-check-input ${validationErrors.acceptedTerms ? 'is-invalid' : ''}`}
                                />
                                <label htmlFor="acceptTermsCheck" className="form-check-label small text-white" style={{ fontSize: '0.75rem', lineHeight: '1.4' }}>
                                    He leído y acepto los <button type="button" className="btn btn-link p-0 text-warning small border-0 align-baseline text-decoration-underline" style={{ fontSize: '0.75rem' }} onClick={() => setShowTermsModal(true)}>Términos y Condiciones</button> y el <button type="button" className="btn btn-link p-0 text-warning small border-0 align-baseline text-decoration-underline" style={{ fontSize: '0.75rem' }} onClick={() => setShowPrivacyModal(true)}>Aviso de Privacidad</button> de la dinámica.
                                </label>
                                <label className="form-check-label small text-white" style={{ fontSize: '0.75rem', lineHeight: '1.4' }}>
                                </label>
                                {validationErrors.acceptedTerms && (
                                    <div className="invalid-feedback small d-block mt-1">{validationErrors.acceptedTerms}</div>
                                )}
                            </div>

                            <button type="submit" className="btn QuizzB9ZoneFan__BtnGold w-100 mb-3">
                                <i className="bi bi-play-fill me-1 fs-5"></i> {config?.gameType === 'TRIVIA' ? 'Iniciar Trivia' : '¡Jugar Ahora!'}
                            </button>
                        </form>

                        {/* ENLACE DISCRETO DE RECUPERACIÓN DE PASE */}
                        <div className="text-center pt-4 mt-3 border-top border-secondary border-opacity-25">
                            <button
                                type="button"
                                className="btn btn-link text-white text-decoration-none small fw-bold p-0"
                                onClick={() => {
                                    setRecoverError('');
                                    setRecoverQuery('');
                                    setShowRecoverModal(true);
                                }}
                            >
                                <i className="bi bi-ticket-perforated-fill me-1"></i> ¿Ya habías ganado un pase? Consulta y recupéralo aquí
                            </button>
                        </div>
                    </div>
                </main>
            )}

            {/* ============================================================ */}
            {/* FASE: EVALUANDO DUPLICADOS PREVENTIVO                        */}
            {/* ============================================================ */}
            {phase === 'evaluating_duplicate' && (
                <main className="d-flex flex-column align-items-center justify-content-center flex-grow-1" style={{ minHeight: '60vh' }}>
                    <div className="spinner-border text-warning mb-3" style={{ width: '3rem', height: '3rem' }} role="status"></div>
                    <p className="font-cinzel text-warning fs-5 fw-bold tracking-wider">Verificando registro...</p>
                </main>
            )}

            {/* ============================================================ */}
            {/* MODAL DE RECUPERACIÓN DE PASE (RATE LIMITING INTEGRADO)      */}
            {/* ============================================================ */}
            {showRecoverModal && (
                <div 
                    className="modal show d-block" 
                    tabIndex="-1" 
                    style={{ backgroundColor: 'rgba(0, 0, 0, 0.75)', backdropFilter: 'blur(6px)', zIndex: 1060 }}
                >
                    <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '420px', margin: '1rem auto' }}>
                        <div 
                            className="modal-content text-white" 
                            style={{ 
                                background: 'rgba(23, 37, 84, 0.97)', 
                                border: '1px solid #d4af37', 
                                borderRadius: '1.25rem', 
                                boxShadow: '0 20px 40px rgba(0,0,0,0.6)' 
                            }}
                        >
                            <div className="modal-header border-0 pb-0">
                                <h5 className="modal-title font-cinzel gold-text fw-bold">
                                    <i className="bi bi-ticket-perforated me-2"></i> Recuperar mi pase
                                </h5>
                                <button 
                                    type="button" 
                                    className="btn-close btn-close-white" 
                                    onClick={() => setShowRecoverModal(false)}
                                ></button>
                            </div>
                            <div className="modal-body py-3">
                                <p className="small text-white mb-3">
                                    Ingresa el correo electrónico o teléfono (10 dígitos) con el que participaste para consultar tu pase exclusivo:
                                </p>

                                {recoverError && (
                                    <div className="alert alert-danger py-2 px-3 small rounded-3 mb-3 fw-bold">
                                        <i className="bi bi-exclamation-triangle-fill me-2"></i> {recoverError}
                                    </div>
                                )}

                                <form onSubmit={handleRecoverSubmit}>
                                    <div className="mb-3">
                                        <input
                                            type="text"
                                            className="form-control QuizzB9ZoneFan__Input"
                                            //placeholder="tu.correo@ejemplo.com o 4441234567"
                                            placeholder="Escríbelo aquí..."
                                            value={recoverQuery}
                                            onChange={(e) => {
                                                setRecoverQuery(e.target.value);
                                                if (recoverError) setRecoverError('');
                                            }}
                                            disabled={recoverLoading}
                                            autoFocus
                                        />
                                    </div>
                                    <button 
                                        type="submit" 
                                        className="btn QuizzB9ZoneFan__BtnGold w-100"
                                        disabled={recoverLoading}
                                    >
                                        {recoverLoading ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                                Consultando...
                                            </>
                                        ) : (
                                            <>
                                                <i className="bi bi-search me-1"></i> Buscar mi Pase
                                            </>
                                        )}
                                    </button>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ============================================================ */}
            {/* 3. FASE DE TRIVIA DINÁMICA                                  */}
            {/* ============================================================ */}
            {phase === 'trivia' && questions.length > 0 && currentQuestion && (
                <main className="QuizzB9ZoneFan__TriviaContainer animate__animated animate__fadeIn flex-grow-1 w-100">
                    <div className="mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <span className="small text-white-50 text-uppercase fw-bold">
                                Pregunta {currentQuestionIndex + 1} de {questions.length}
                            </span>
                            <span className="badge bg-warning text-dark font-cinzel fw-bold px-2 py-1">
                                {Math.round(((currentQuestionIndex + 1) / questions.length) * 100)}%
                            </span>
                        </div>
                        <div className="QuizzB9ZoneFan__ProgressBar">
                            <div 
                                className="QuizzB9ZoneFan__ProgressBarFill"
                                style={{ width: `${((currentQuestionIndex + 1) / questions.length) * 100}%` }}
                            ></div>
                        </div>
                    </div>

                    <div className="QuizzB9ZoneFan__Card mb-4">
                        {currentQuestion.showLogo && (
                            <div className="text-center mb-3">
                                <img 
                                    //src="/img/brand/logotipo-adsl-blanco.png" 
                                    src="/img/page/adsl-five.png" 
                                    alt="ADSL" 
                                    style={{ maxHeight: '55px' }} 
                                    onError={(e) => { e.target.src = "/img/page/adsl-five.png"; }}
                                />
                            </div>
                        )}

                        <h3 className="fs-5 fw-bold text-center text-white mb-4 leading-snug">
                            {currentQuestion.instruction}
                        </h3>

                        <div className="d-flex flex-column gap-3" key={`q-container-${currentQuestion.id}`}>
                            {currentQuestion.options.map((option, idx) => {
                                const isSelected = selectedOptionTemp === option.id;
                                return (
                                    <button
                                        key={`q-${currentQuestion.id}-opt-${option.id}`}
                                        type="button"
                                        className={`QuizzB9ZoneFan__OptionBtn ${isSelected ? 'QuizzB9ZoneFan__OptionBtn--Selected' : ''}`}
                                        onClick={(e) => {
                                            if (e && e.currentTarget && typeof e.currentTarget.blur === 'function') {
                                                e.currentTarget.blur();
                                            }
                                            handleSelectOption(currentQuestion.id, option.id);
                                        }}
                                    >
                                        <span className="d-flex align-items-center">
                                            <span className="badge rounded-circle bg-secondary me-3" style={{ width: '28px', height: '28px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                                                {String.fromCharCode(65 + idx)}
                                            </span>
                                            {option.text}
                                        </span>
                                        {isSelected && <i className="bi bi-check-circle-fill text-warning fs-5"></i>}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </main>
            )}

            {/* ============================================================ */}
            {/* 3.1. FASE DE MINIJUEGO INTERACTIVO (VENCE AL DIABLO)         */}
            {/* ============================================================ */}
            {phase === 'minigame' && (
                <main className="QuizzB9ZoneFan__MinigameContainer animate__animated animate__fadeIn flex-grow-1 w-100 d-flex flex-column align-items-center justify-content-center p-0" style={{ minHeight: '80vh', width: '100%' }}>
                    <PluckingRoosterGame 
                        initialDuration={config?.gameDurationSeconds || 15}
                        onGameFinish={handleMinigameFinish}
                    />
                </main>
            )}

            {/* ============================================================ */}
            {/* 4. FASE DE ENVÍO / EVALUANDO                                */}
            {/* ============================================================ */}
            {phase === 'submitting' && (
                <main className="d-flex flex-column align-items-center justify-content-center flex-grow-1" style={{ minHeight: '60vh' }}>
                    <div className="spinner-border text-warning mb-4" style={{ width: '3.5rem', height: '3.5rem' }} role="status"></div>
                    <h2 className="font-cinzel text-warning fs-4 fw-bold tracking-wider mb-2">
                        {config?.gameType === 'TRIVIA' ? 'Evaluando tus respuestas...' : 'Evaluando tu puntuación...'}
                    </h2>
                    <p className="text-white-50 small">Consultando disponibilidad de pases exclusivos</p>
                </main>
            )}

            {/* ============================================================ */}
            {/* 5. OVERLAY CAJA DE REGALO SVG INTERACTIVA                    */}
            {/* ============================================================ */}
            {phase === 'gift_overlay' && (
                <div className={`QuizzB9ZoneFan__GiftOverlay ${giftOpened ? 'QuizzB9ZoneFan__GiftOverlay--Opened' : ''}`}>
                    <img 
                        src="/img/page/adsl-five.png"  
                        style={{ width: '350px', marginBottom: '1em' }}
                        alt="ADSL / Five Digital Minds"
                    />
                    <h2 className="font-cinzel fs-2 fs-sm-1 font-black mb-5 gold-text tracking-wider text-center px-4">
                        ¡Tenemos una sorpresa para ti!
                    </h2>

                    <div className="QuizzB9ZoneFan__GiftBox" onClick={handleOpenGift}>
                        <div className="gift-wiggle">
                            <svg className="drop-shadow-2xl overflow-visible" style={{ width: '200px', height: '200px' }} viewBox="0 0 512 512" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <g id="gift-base">
                                    <rect x="96" y="224" width="320" height="256" fill="#b38728" rx="8" />
                                    <rect x="224" y="224" width="64" height="256" fill="#fcf6ba" />
                                </g>
                                <g id="gift-lid">
                                    <rect x="64" y="160" width="384" height="64" fill="#d4af37" rx="8" />
                                    <rect x="224" y="160" width="64" height="64" fill="#fcf6ba" />
                                </g>
                                <g id="gift-bow">
                                    <path d="M256 160 C256 64 128 64 128 160 C128 200 192 160 256 160 Z" fill="#fcf6ba" />
                                    <path d="M256 160 C256 64 384 64 384 160 C384 200 320 160 256 160 Z" fill="#fcf6ba" />
                                    <path d="M256 160 C256 112 176 112 176 160 Z" fill="#d4af37" />
                                    <path d="M256 160 C256 112 336 112 336 160 Z" fill="#d4af37" />
                                </g>
                            </svg>
                        </div>
                        <p className="text-center mt-4 text-white font-bold tracking-widest uppercase small animate-pulse">
                            <i className="bi bi-hand-index-thumb-fill me-1"></i> Toca el regalo para descubrirla
                        </p>
                    </div>

                    <footer className="text-center py-3" style={{ position: 'fixed', bottom: '1em'}}>
                        <p className="text-center FdmFooter FooterCredits mb-0">
                            <a href="https://interaktia.com/?utm_source=quizz_b9_zone_web&utm_medium=footer&utm_campaign=fcm_doradosfc" target="_blank" rel="noreferrer"
                            className="text-white">
                                Hecho con ♥ por Interaktia | 2026
                            </a>
                        </p>
                    </footer>
                </div>
            )}

            {phase === 'pass_revealed' && (activePass || storedPasses.length > 0) && (() => {
                const currentPass = activePass || storedPasses[activePassIndex];

                // Priorizar la configuración activa del servidor para reflejar cambios del operador de inmediato
                const showQr = config?.showPassQr !== undefined 
                    ? config.showPassQr 
                    : (currentPass?.showPassQr !== undefined ? currentPass.showPassQr : true);

                const showUserData = config?.showPassUserData !== undefined 
                    ? config.showPassUserData 
                    : (currentPass?.showPassUserData !== undefined ? currentPass.showPassUserData : false);

                const showLocationNote = config?.showPassLocationNote !== undefined 
                    ? config.showPassLocationNote 
                    : (currentPass?.showPassLocationNote !== undefined ? currentPass.showPassLocationNote : false);

                const isMinimal = !showQr && !showUserData;

                return (
                <main className="container py-3 px-3 animate__animated animate__zoomIn flex-grow-1">
                    {storedPasses.length > 1 && (
                        <div className="QuizzB9ZoneFan__MultiPassBar mx-auto mb-3" style={{ maxWidth: '440px' }}>
                            <button
                                type="button"
                                className="btn btn-sm btn-dark text-warning"
                                disabled={activePassIndex <= 0}
                                onClick={() => {
                                    const nextIdx = activePassIndex - 1;
                                    setActivePassIndex(nextIdx);
                                    setActivePass(storedPasses[nextIdx]);
                                }}
                            >
                                <i className="bi bi-chevron-left"></i>
                            </button>
                            <span className="small text-white font-cinzel fw-bold">
                                Pase {activePassIndex + 1} de {storedPasses.length}: <span className="text-warning">{activePass?.name || storedPasses[activePassIndex]?.name}</span>
                            </span>
                            <button
                                type="button"
                                className="btn btn-sm btn-dark text-warning"
                                disabled={activePassIndex >= storedPasses.length - 1}
                                onClick={() => {
                                    const nextIdx = activePassIndex + 1;
                                    setActivePassIndex(nextIdx);
                                    setActivePass(storedPasses[nextIdx]);
                                }}
                            >
                                <i className="bi bi-chevron-right"></i>
                            </button>
                        </div>
                    )}

                    {/* BOTÓN SUPERIOR DE GUARDAR PASE EN GALERÍA */}
                    <div className="text-center mb-3 mx-auto" style={{ maxWidth: '440px' }}>
                        <p className="small text-white mb-2" style={{ fontSize: '0.78rem' }}>
                            Aquí está tu pase de acceso. Guárdalo o toma captura de pantalla
                        </p>
                        <button
                            type="button"
                            className="btn btn-warning w-100 py-3 rounded-3 fw-black text-uppercase shadow-lg fs-6 d-flex align-items-center justify-content-center gap-2"
                            style={{
                                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                                color: '#ffffff',
                                border: 'none',
                                letterSpacing: '0.04em'
                            }}
                            onClick={handleDownloadPassCard}
                            disabled={isSavingImage}
                        >
                            {isSavingImage ? (
                                <>
                                    <span className="spinner-border spinner-border-sm" role="status"></span>
                                    Guardando en tu galería...
                                </>
                            ) : (
                                <>
                                    <i className="bi bi-camera-fill fs-5"></i>
                                    📸 Guardar Pase en mi Galería
                                </>
                            )}
                        </button>
                    </div>

                    <div id="pass-card" className="QuizzB9ZoneFan__PassCard">
                        <h1 className="font-cinzel text-4xl font-black mb-1 gold-text tracking-wider">
                            ¡Felicidades!
                        </h1>

                        <div className="QuizzB9ZoneFan__Divider"></div>

                        <div className="mb-3 px-1">
                            <p className="fs-6 sun-glare-fix leading-snug">
                                Ganaste una convivencia en "La Zona" al <strong className="text-danger fw-black">finalizar el partido</strong>.
                            </p>
                        </div>

                        <div className="QuizzB9ZoneFan__ShimmerBanner">
                            <div className="QuizzB9ZoneFan__ShimmerBannerOverlay"></div>
                            <h2 className="font-cinzel fs-4 font-black text-white text-uppercase tracking-widest mb-0 drop-shadow">
                                Pase Exclusivo Personal
                            </h2>
                            <p className="text-white small fw-bold mt-1 mb-2 tracking-wide">
                                {activePass?.matchTitle || storedPasses[activePassIndex]?.matchTitle || 'Dorados de Sinaloa'}
                            </p>
                            <div className="QuizzB9ZoneFan__FolioBadge">
                                {activePass?.passCode || storedPasses[activePassIndex]?.passCode || '#FIEXARA01'}
                            </div>
                        </div>

                        {showQr && (
                            <div className="d-flex justify-content-center my-3 p-2 bg-light rounded-3 border">
                                <QRCodeSVG
                                    value={activePass?.verificationToken || storedPasses[activePassIndex]?.verificationToken || activePass?.passCode || 'FIEX'}
                                    size={175}
                                    bgColor="#ffffff"
                                    fgColor="#172554"
                                    level="H"
                                    includeMargin={true}
                                />
                            </div>
                        )}

                        {showUserData && (
                            <div className="small text-muted mb-2">
                                <span className="fw-bold text-dark">{activePass?.name || storedPasses[activePassIndex]?.name}</span>
                                <br />
                                <span style={{ fontSize: '0.8rem' }}>
                                    {MaskEmail(activePass?.email || storedPasses[activePassIndex]?.email)} | {MaskPhone(activePass?.phone || storedPasses[activePassIndex]?.phone)}
                                </span>
                            </div>
                        )}

                        <div className={`QuizzB9ZoneFan__SecurityBox ${isMinimal ? 'mt-4' : ''}`}>
                            <p>
                                MUESTRA ESTE PASE O UNA CAPTURA AL BAJAR A LA CANCHA
                            </p>
                            {showLocationNote && (
                                <small className="d-block text-danger fw-bold mt-1" style={{ fontSize: '0.75rem' }}>
                                    Túnel 2 al finalizar el partido
                                </small>
                            )}
                        </div>

                        <div className="mt-3">
                            <p className="text-muted uppercase mb-2 fw-bold" style={{ fontSize: '0.65rem', letterSpacing: '0.15em' }}>
                                PATROCINADO POR
                            </p>
                            {defaultLogos}
                        </div>
                    </div>

                    {/* BOTÓN INFERIOR DE GUARDAR PASE EN GALERÍA */}
                    <div className="text-center mt-3 mx-auto" style={{ maxWidth: '440px' }}>
                        <button
                            type="button"
                            className="btn btn-warning w-100 py-3 rounded-3 fw-black text-uppercase shadow-lg fs-6 d-flex align-items-center justify-content-center gap-2"
                            style={{
                                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                                color: '#ffffff',
                                border: 'none',
                                letterSpacing: '0.04em'
                            }}
                            onClick={handleDownloadPassCard}
                            disabled={isSavingImage}
                        >
                            {isSavingImage ? (
                                <>
                                    <span className="spinner-border spinner-border-sm" role="status"></span>
                                    Guardando en tu galería...
                                </>
                            ) : (
                                <>
                                    <i className="bi bi-camera-fill fs-5"></i>
                                    📸 Guardar Pase en mi Galería
                                </>
                            )}
                        </button>
                        <p className="small mt-2 mb-0" style={{ fontSize: '0.78rem' }}>
                            Aquí está tu pase de acceso. Guárdalo o toma captura de pantalla
                        </p>
                    </div>
                </main>
                );
            })()}

            {/* ============================================================ */}
            {/* 7. PANTALLA: YA PARTICIPÓ PREVIAMENTE                        */}
            {/* ============================================================ */}
            {phase === 'already_participated' && (
                <main className="QuizzB9ZoneFan__ThanksContainer animate__animated animate__fadeIn flex-grow-1 w-100">
                    <div className="QuizzB9ZoneFan__Card text-center py-5">
                        <i className="bi bi-patch-check-fill text-success fs-1 mb-3"></i>
                        <h2 className="font-cinzel text-white fs-3 fw-bold mb-3">
                            ¡Gracias por participar!
                        </h2>
                        <p className="text-white mb-3 px-2">
                            Tus respuestas ya fueron recibidas anteriormente para este partido.
                        </p>
                        <p className="text-white mb-4 px-2 small">
                            En caso de haber resultado ganador(a) y haber sido elegid@, te debió haber aparecido tu pase exclusivo y personal. Guárdalo ya que será tu pase de acceso.
                        </p>
                        <p className="text-white mb-4 px-2 small">
                            ¿Ganaste, fuiste elegid@ pero no cuentas con tu pase? Puedes consultarlo aquí
                        </p>
                        <button
                            type="button"
                            className="btn btn-outline-warning rounded-pill px-4 fw-bold"
                            onClick={() => {
                                setRecoverError('');
                                setRecoverQuery('');
                                setShowRecoverModal(true);
                            }}
                        >
                            <i className="bi bi-ticket-perforated-fill me-1"></i> Consultar mi pase
                        </button>
                    </div>
                </main>
            )}

            {/* ============================================================ */}
            {/* 8. PANTALLA DE AGRADECIMIENTO (NO GANADOR)                   */}
            {/* ============================================================ */}
            {phase === 'thanks' && (
                <main className="QuizzB9ZoneFan__ThanksContainer animate__animated animate__fadeIn flex-grow-1 w-100">
                    <div className="QuizzB9ZoneFan__Card text-center py-5">
                        <i className="bi bi-heart-fill text-danger fs-1 mb-3"></i>
                        <h2 className="font-cinzel text-white fs-3 fw-bold mb-3">
                            ¡Muchas gracias por participar!
                        </h2>
                        {lastScoreAchieved > 0 && config?.winnerSelectionMode === 'SCORE_THRESHOLD' && (
                            <div className="badge bg-danger-subtle text-danger border border-danger-subtle p-2 mb-3 fs-6 fw-bold">
                                Lograste {lastScoreAchieved} puntos
                            </div>
                        )}
                        <p className="text-white-50 mb-4 px-2">
                            Agradecemos tu pasión y apoyo al Dorados de Sinaloa. Sigue atento a las pantallas del estadio para más dinámicas exclusivas durante el partido.
                        </p>
                    </div>
                </main>
            )}

            {/* ============================================================ */}
            {/* 9. PANTALLA: TRIVIA FINALIZADA / CERRADA                     */}
            {/* ============================================================ */}
            {phase === 'ended' && (
                <main className="QuizzB9ZoneFan__ThanksContainer animate__animated animate__fadeIn flex-grow-1 w-100">
                    <div className="QuizzB9ZoneFan__Card text-center py-5">
                        <i className="bi bi-calendar-x-fill text-warning fs-1 mb-3"></i>
                        <h2 className="font-cinzel text-white fs-3 fw-bold mb-3">
                            Dinámica Cerrada
                        </h2>
                        <p className="text-white-50 mb-4 px-2">
                            La dinámica "Convivencia en La Zona" ha finalizado por ahora. ¡Muchas gracias por tu interés y pasión por el club!
                        </p>
                        <button
                            type="button"
                            className="btn btn-outline-warning rounded-pill px-4 fw-bold"
                            onClick={() => {
                                setRecoverError('');
                                setRecoverQuery('');
                                setShowRecoverModal(true);
                            }}
                        >
                            <i className="bi bi-ticket-perforated-fill me-1"></i> Consultar pase ganado
                        </button>
                    </div>
                </main>
            )}

            {/* ============================================================ */}
            {/* 10. PANTALLA DE ERROR                                       */}
            {/* ============================================================ */}
            {phase === 'error' && (
                <main className="QuizzB9ZoneFan__ThanksContainer animate__animated animate__fadeIn flex-grow-1 w-100">
                    <div className="QuizzB9ZoneFan__Card text-center py-5">
                        <i className="bi bi-exclamation-triangle-fill text-warning fs-1 mb-3"></i>
                        <h2 className="font-cinzel text-white fs-3 fw-bold mb-3">
                            Ha ocurrido un detalle
                        </h2>
                        <p className="text-white-50 mb-4 px-2">
                            {apiErrorMessage || 'No fue posible procesar tu participación en este momento.'}
                        </p>
                        <button
                            type="button"
                            className="btn btn-outline-warning rounded-pill px-4 fw-bold"
                            onClick={() => setPhase('register')}
                        >
                            <i className="bi bi-arrow-repeat me-1"></i> Reintentar
                        </button>
                    </div>
                </main>
            )}

            {/* FOOTER */}
            <footer className="text-center py-3">
                <p className="text-center FdmFooter FooterCredits mb-0">
                    <a href="https://interaktia.com/?utm_source=quizz_b9_zone_web&utm_medium=footer&utm_campaign=fcm_doradosfc" target="_blank" rel="noreferrer"
                    className="text-white">
                        Hecho con ♥ por Interaktia | 2026
                    </a>
                </p>
            </footer>
        </div>
    );
};

export default QuizzB9ZoneFan;
